import { IBrokerProps } from 'app/Pages/common';
import { cloneObject, preflightAPICall, processAPIError, translate } from 'utils';
import axios, { AxiosError } from 'axios';
import { TError, TFetchStatus } from 'models';
import React, { useEffect, useRef, useState } from 'react';
import { TInsurer } from 'app/Pages/User/profile/body/Insurers';
import { API_GET_ALL_ORDERED_INSURERS } from 'app/Components/common/InsurersList';
import LoadingOverlay from 'helpers/LoadingOverlay';
import { renderErrors } from 'app/Components/common/Error';
import {
  InsurersControlsBlockStyled,
  ControlButtonStyled,
  InsurersContainerStyled,
  InsurersItemStyled,
} from './styles';

interface ISortingSettings {
  onSubmit: (props: IBrokerProps, onError: (errors: AxiosError) => void, onSuccess: () => void) => void,
  props?: IBrokerProps,
}

export const SortingSettings = ({ onSubmit, props }: ISortingSettings) => {
  const [fetchInsurersStatus, updateFetchInsurersStatus] = useState<TFetchStatus>(null);
  const [errors, updateErrors] = useState<TError[]>([]);
  const [insurersList, updateInsurersList] = useState<string[]>([]);
  const [sortableInsurersList, updateSortableInsurersList] = useState<string[]>([]);
  const [isTouched, updateIsTouched] = useState(false);

  const dragItem = useRef<null | number>(null);
  const dragOverItem = useRef<null | number>(null);

  useEffect(() => {
    getAllInsurers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onError = (error: AxiosError) => {
    updateErrors(() => processAPIError(error, true) as TError[]);
    updateFetchInsurersStatus('failed');
  };

  const onSuccess = () => {
    updateFetchInsurersStatus('success');
    updateIsTouched(false);
  };

  const processInsurersList = (defaultInsurersList: string[]) => {
    let insurersListToSet: string[];

    if (props?.sorted_insurer_list) {
      const brokerInsurersList = [...props?.sorted_insurer_list];

      if (defaultInsurersList.length === brokerInsurersList.length) {
        if (brokerInsurersList.some(insurer => !defaultInsurersList.includes(insurer))) {
          insurersListToSet = defaultInsurersList;
        } else {
          insurersListToSet = brokerInsurersList;
        }
      } else {
        insurersListToSet = defaultInsurersList;
      }
    } else {
      insurersListToSet = defaultInsurersList;
    }

    updateInsurersList(insurersListToSet);
    updateSortableInsurersList(insurersListToSet);
  };

  const getAllInsurers = () => {
    updateFetchInsurersStatus('loading');
    preflightAPICall(() => {
      axios.get(API_GET_ALL_ORDERED_INSURERS)
        .then(response => {
          processInsurersList(response.data.results.map((insurer: TInsurer) => insurer.tag));
          updateFetchInsurersStatus('success');
        }).catch(onError);
    });
  };

  const handleSubmit = () => {
    updateFetchInsurersStatus('loading');

    const _props = props ? cloneObject(props) : {};

    _props.sorted_insurer_list = sortableInsurersList;
    onSubmit(_props, onError, onSuccess);
  };

  const onCancel = () => {
    updateSortableInsurersList([...insurersList]);
    updateIsTouched(false);
  };

  const dragStart = (e: React.DragEvent<HTMLDivElement>, position: number) => {
    dragItem.current = position;
  };

  const dragEnter = (e: React.DragEvent<HTMLDivElement>, position: number) => {
    dragOverItem.current = position;

    const targetElement = e.target as Element;
    const _sortableInsurersList = [...sortableInsurersList];
    const dragItemContent = _sortableInsurersList[dragItem.current as number];

    _sortableInsurersList.splice(dragItem.current as number, 1);
    _sortableInsurersList.splice(dragOverItem.current as number, 0, dragItemContent);

    targetElement.classList.add('drag-over');
    dragItem.current = dragOverItem.current;
    dragOverItem.current = null;

    updateSortableInsurersList(_sortableInsurersList);
  };

  const dragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    const targetElement = e.target as Element;
    targetElement.classList.remove('drag-over');
  };

  const dragEnd = (e: React.DragEvent<HTMLDivElement>) => {
    const targetElement = e.target as Element;
    targetElement.classList.remove('drag-over');
    updateIsTouched(true);
  };

  if (fetchInsurersStatus === 'loading') {
    return <LoadingOverlay />;
  }

  if (fetchInsurersStatus === 'failed') {
    return <>{renderErrors(errors)}</>;
  }

  return (
    <InsurersContainerStyled>
      {!!sortableInsurersList.length && sortableInsurersList.map((insurer, index) => (
        <InsurersItemStyled
          key={insurer}
          draggable
          onDragStart={(e) => dragStart(e, index)}
          onDragOver={(e) => e.preventDefault()}
          onDragEnter={(e) => dragEnter(e, index)}
          onDragLeave={dragLeave}
          onDragEnd={dragEnd}
        >
          {insurer}
        </InsurersItemStyled>
      ))}
      {isTouched && (
        <InsurersControlsBlockStyled>
          <ControlButtonStyled size="l" preset="secondary" onClick={onCancel}>
            {translate({ key: 'common.button.cancel' })}
          </ControlButtonStyled>
          <ControlButtonStyled size="l" onClick={handleSubmit}>
            {translate({ key: 'common.button.save' })}
          </ControlButtonStyled>
        </InsurersControlsBlockStyled>
      )}
    </InsurersContainerStyled>
  );
};
